import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Button, DatePicker, Input} from 'antd';
import DBModal from 'components/modal/modal';
import {BULK_FULFIL_OR_REJECT_SWAPS} from 'graphql/mutations/wallet';
import {GET_SWAP_TRANSACTION_SUMMARY} from 'graphql/queries/wallet';
import {
  buildSwapFilters,
  describeSwapFilters,
  submitBulkSwapJob,
} from './swapReporting';
import {SwapBulkJobDetails} from './SwapBulkJob';
import styles from './swapTransactions.module.scss';

const MAX_REASON = 500;
// The backend refuses a selection larger than this.
const MAX_IDS = 5000;

/**
 * Confirms one bulk fulfil or reject, starts it as a backend job, and follows
 * that job until it finishes — or until the admin closes this, which does not
 * stop it.
 *
 * `request` is `{status: 'SUCCESSFUL' | 'FAILED', ids}` for selected rows, or
 * `{status, allMatching: true, currencies}` for every pending swap in those
 * currencies. A bulk update can be narrowed to dates picked here; those are
 * the dates sent, never the page's filter, and leaving them empty means every
 * date. The pending count is fetched for exactly that scope, so the number on
 * the button is what will be processed. Wallet balances move, so nothing is
 * sent until the admin has read the scope and pressed the button, and then
 * exactly once: a lost response is never retried, because the first request
 * may already have started a job.
 */
const SwapBulkModal = ({
  request,
  onClose,
  onStarted,
  onFinished,
  onShowRecentJobs,
}) => {
  const [dates, setDates] = useState(undefined);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The job receipt once started; or `{error, uncertain}` if it was not.
  const [job, setJob] = useState(null);
  const [failure, setFailure] = useState(null);
  const [mutate] = useMutation(BULK_FULFIL_OR_REJECT_SWAPS);

  useEffect(() => {
    if (request) {
      setReason('');
      setDates(undefined);
      setTouched(false);
      setSubmitting(false);
      setJob(null);
      setFailure(null);
    }
  }, [request]);

  const allFilters = request?.allMatching
    ? buildSwapFilters({...request.currencies, dates})
    : null;
  const {data: countData, loading: counting} = useQuery(
    GET_SWAP_TRANSACTION_SUMMARY,
    {
      variables: {input: allFilters},
      skip: !allFilters,
      fetchPolicy: 'no-cache',
    },
  );
  const countResult = countData?.getSwapTransactionSummary;
  const expected =
    countResult?.__typename === 'SwapTransactionSummary'
      ? countResult.pending.count
      : undefined;

  if (!request) return null;

  const rejecting = request.status === 'FAILED';
  const verb = rejecting ? 'Reject' : 'Fulfil';
  const reasonMissing = rejecting && !reason.trim();
  const tooMany = (request.ids?.length || 0) > MAX_IDS;
  // Once a request has gone out, this dialog never sends another one.
  const sent = submitting || Boolean(job) || Boolean(failure?.uncertain);

  const start = async () => {
    setTouched(true);
    if (reasonMissing || tooMany || sent) return;
    setSubmitting(true);
    const outcome = await submitBulkSwapJob({
      mutate,
      input: {
        status: request.status,
        ...(request.ids
          ? {ids: request.ids}
          : {allMatching: true, filters: {...allFilters, status: 'PENDING'}}),
        ...(rejecting && {reasonForFailure: reason.trim()}),
      },
    });
    setSubmitting(false);
    if (outcome.job) {
      setJob(outcome.job);
      onStarted?.(outcome.job);
    } else {
      setFailure(outcome);
      // A request that may have landed can only be checked, not repeated.
      if (outcome.uncertain) onStarted?.(null);
    }
  };

  return (
    <DBModal
      isOpen
      width={560}
      destroyOnClose
      handleClose={submitting ? undefined : onClose}
      content={
        <div className={styles.bulkModal}>
          <h2>
            {verb} {request.ids ? 'selected swaps' : 'pending swaps in bulk'}
          </h2>

          {!job && !failure?.uncertain && (
            <>
              <p className={styles.bulkScope}>
                {verb === 'Fulfil'
                  ? 'Adds each converted amount to the user’s wallet in the new currency.'
                  : 'Gives each user back the amount they set aside to swap.'}{' '}
                This will {verb.toLowerCase()}{' '}
                <strong>
                  {request.ids
                    ? `${request.ids.length} selected pending swap${
                        request.ids.length === 1 ? '' : 's'
                      }`
                    : `every pending swap ${describeSwapFilters({
                        ...request.currencies,
                        dates,
                      })}`}
                </strong>
                {!request.ids
                  ? counting
                    ? ' — counting…'
                    : typeof expected === 'number'
                    ? ` — ${expected.toLocaleString(
                        'en-US',
                      )} right now, including ones on other pages.`
                    : '.'
                  : '.'}
              </p>
              {!request.ids && (
                <div className={styles.bulkField}>
                  <label htmlFor="swap-bulk-dates">
                    Created between (optional)
                  </label>
                  <DatePicker.RangePicker
                    id="swap-bulk-dates"
                    allowEmpty={[true, true]}
                    format="D MMM YYYY"
                    placeholder={['Any start', 'Any end']}
                    value={dates}
                    onChange={setDates}
                  />
                  <span className={styles.bulkFieldHint}>
                    Leave empty to include pending swaps from every date.
                  </span>
                </div>
              )}
              {rejecting && (
                <div className={styles.bulkField}>
                  <label htmlFor="swap-bulk-reason">
                    Reason for rejection (required)
                  </label>
                  <Input.TextArea
                    id="swap-bulk-reason"
                    rows={3}
                    maxLength={MAX_REASON}
                    showCount
                    value={reason}
                    placeholder="e.g. Unable to complete the requested swap"
                    onChange={event => setReason(event.target.value)}
                  />
                  {touched && reasonMissing && (
                    <span className={styles.bulkError}>Give a reason.</span>
                  )}
                </div>
              )}
            </>
          )}

          {tooMany && (
            <Alert
              type="error"
              showIcon
              message={`At most ${MAX_IDS.toLocaleString(
                'en-US',
              )} swaps can be selected at once. Use Bulk update pending instead.`}
            />
          )}

          {failure && !failure.uncertain && (
            <Alert
              type="error"
              showIcon
              message="The job was not started"
              description={failure.error}
            />
          )}

          {failure?.uncertain && (
            <Alert
              type="warning"
              showIcon
              message="We could not confirm whether the job started"
              description={`${failure.error} The request may still have reached the server and started a job. Check Recent bulk jobs before trying again — sending it twice could process these swaps twice.`}
            />
          )}

          {job && (
            <SwapBulkJobDetails
              jobId={job.jobId}
              initialJob={job}
              onTerminal={onFinished}
            />
          )}

          <div className={styles.bulkActions}>
            {job || failure?.uncertain ? (
              <>
                {failure?.uncertain && (
                  <Button onClick={onShowRecentJobs}>Recent bulk jobs</Button>
                )}
                <Button type="primary" onClick={onClose}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  danger={rejecting}
                  loading={submitting}
                  // Nothing to send until the scope's count is known, or when
                  // it holds no pending swaps at all.
                  disabled={
                    sent || tooMany || (!request.ids && (counting || !expected))
                  }
                  onClick={start}>
                  {verb}{' '}
                  {request.ids
                    ? `${request.ids.length} swap${
                        request.ids.length === 1 ? '' : 's'
                      }`
                    : typeof expected === 'number'
                    ? `${expected.toLocaleString('en-US')} swap${
                        expected === 1 ? '' : 's'
                      }`
                    : 'pending swaps'}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    />
  );
};

export default SwapBulkModal;
